import { forwardRef } from '@chakra-ui/react-v2';
import type { TooltipProps } from '@chakra-ui/react-v2';
import { chakra, useMultiStyleConfig } from '@chakra-ui/system';
import { cx } from '@chakra-ui/utils';
import { Fragment } from 'react/jsx-runtime';

import { Tooltip } from '@frontend/legacy-components/src/components/overlay/tooltip/Tooltip';

import { usePercentageBreakdownContext } from './context';

export type PercentageBreakdownTooltipProps = TooltipProps & {
  additionalText?: React.ReactNode;
};

/**
 * PercentageBreakdownTooltip is a wrapper component that provides a tooltip
 * with percentage breakdown information. It should wrap other components
 * like PercentageBreakdownBar or icons.
 */
export const PercentageBreakdownTooltip = forwardRef<
  PercentageBreakdownTooltipProps,
  'div'
>(({ className, children, additionalText, ...props }, ref) => {
  const { data, size, variant } = usePercentageBreakdownContext();
  const styles = useMultiStyleConfig('PercentageBreakdown', { size, variant });
  const _className = cx('percentage-breakdown__tooltip', className);

  // Create tooltip content
  const tooltipContent = (
    <>
      <chakra.div __css={styles.tooltip} className={_className} ref={ref}>
        {data.map((item) => (
          <Fragment key={`${item.label}-${item.value}-${item.color}`}>
            <chakra.span
              __css={styles.tooltipItemLabel}
              className="percentage-breakdown__tooltip-item-label"
            >
              <chakra.div
                __css={styles.tooltipItemDot}
                bg={item.color}
                className="percentage-breakdown__tooltip-item-dot"
              />
              {item.label}
            </chakra.span>
            <chakra.span
              __css={styles.tooltipItemValue}
              className="percentage-breakdown__tooltip-item-value"
            >
              {item.displayValue ?? item.value}
            </chakra.span>
          </Fragment>
        ))}
      </chakra.div>

      {/* Only render additional text if provided */}
      {additionalText && (
        <chakra.div
          __css={styles.tooltipMessage}
          className="percentage-breakdown__tooltip-additional-text"
        >
          {additionalText}
        </chakra.div>
      )}
    </>
  );

  return (
    <Tooltip
      label={tooltipContent}
      placement="bottom"
      {...props}
      maxWidth="min-content"
    >
      {children}
    </Tooltip>
  );
});
PercentageBreakdownTooltip.displayName = 'PercentageBreakdownTooltip';
